import { useTypedParams } from 'ClientPortal/src/router/useTypedParams'
import {
    assembleAssetTitle,
    ContactList,
    ContentChip,
    CustomerUserAssignment,
    DocumentTable,
    EmptyState,
    FileUploadModal,
    HeaderObservers,
    HeadlineWithSpacing,
    ImageGallery,
    joinDefinedStrings,
    MandatorList,
    ObserversDrawer,
    PageRowTemplate,
    TabularInfo,
    TaskListPreview,
    ThreadListPreview,
    tokens,
    getUserSettingsModalUrl,
} from 'ComponentLibrary'
import { useOrderDetailsPageData } from 'ClientPortal/src/pages/Orders/OrderDetails/OrderDetailsPage.data'
import { Box, GridLegacy as Grid, Stack } from '@mui/material'
import { OrderStatusInfoBox } from 'ClientPortal/src/pages/Orders/OrderDetails/OrderDetailsInfoBox/OrderStatusInfoBox'
import { useLocation, useNavigate } from 'react-router-dom'
import { useRef, useState } from 'react'
import { AssetInfoBox } from 'ClientPortal/src/pages/Orders/OrderDetails/OrderDetailsInfoBox/AssetInfoBox'
import { ClientPortalPageWithSidebarTemplate } from 'ClientPortal/src/pages/Orders/ClientPortalPageWithSidebarTemplate'
import { OrderSidebar } from 'ClientPortal/src/pages/Orders/OrderSidebar'
import { STATIC_APP_PATHS } from 'ClientPortal/src/router/constants'
import { getAppraiserUIBaseUrl } from 'ClientPortal/src/common/environment'
import {
    ArrowUpRightIcon,
    AtIcon,
    ChatsCircleIcon,
    ClipboardTextIcon,
    CopyIcon,
    DownloadSimpleIcon,
    EnvelopeIcon,
    EyeIcon,
    EyeSlashIcon,
    PencilSimpleIcon,
    PlusCircleIcon,
    PlusIcon,
    UserCircleIcon,
} from '@phosphor-icons/react'
import { OrderDetailsPageLoading } from 'ClientPortal/src/pages/Orders/OrderDetails/OrderDetailsPage.load'
import LoadingIndicator from 'ComponentLibrary/src/atoms/LoadingIndicator/LoadingIndicator'
import { getLatestUrlParams } from 'ComponentLibrary/src/hooks/useUrlState'
import { IconButton, IconButtonWithOptions, TinyButton } from 'ComponentLibrary/src/piazza/buttons'
import { useFormQueryParams } from 'ClientPortal/src/router/useFormQueryParams'
import { Option } from 'ComponentLibrary/src/piazza/buttons/IconButton/IconButtonWithOptions'
import { validate as isUuid } from 'uuid'
import { Typography } from 'ComponentLibrary/src/piazza/text'
import { isEmpty } from 'lodash'
import { ValuationSection } from 'ClientPortal/src/pages/Orders/OrderDetails/ValuationSection'
import { Headline } from 'ComponentLibrary/src/piazza/text/Headline/Headline'
import { AppraiserSection } from 'ClientPortal/src/pages/Orders/OrderDetails/AppraiserSection/AppraiserSection'
import { useTranslation } from 'react-i18next'
import { useUserLinkCopy } from 'ClientPortal/src/i18n/useUserLinkCopy'
import { useDocumentTagOptions } from 'ClientPortal/src/i18n/useDocumentTagOptions'
import { useCurrentResolvedLocale } from 'ClientPortal/src/i18n/currentLocale'

export function OrderDetailsPage() {
    const { t } = useTranslation('pages', { keyPrefix: 'orders.orderDetailsPage' })
    const { t: tTerminology } = useTranslation('terminology', { keyPrefix: 'common' })
    const documentTagOptions = useDocumentTagOptions()
    const locale = useCurrentResolvedLocale()
    const navigate = useNavigate()
    const location = useLocation()

    const [isAddContactFormOpen, setIsAddContactFormOpen] = useState(false)
    const [isAddMandatorFormOpen, setIsAddMandatorFormOpen] = useState(false)
    const [isEditCustomerAssignmentFormOpen, setIsEditCustomerAssignmentFormOpen] = useState(false)
    const { orderId } = useTypedParams(['orderId'])

    const {
        orderOverview,
        isOrderOverviewLoading,
        getOrderKeyFigures,
        getValuationKeyFigures,
        getImages,
        uploadOrderImages,
        deleteOrderImage,
        isUploadingOrderImages,
        failedImageUploads,
        numImagesToUpload,
        downloadDocument,
        isDownloadingDocument,
        uploadOrderDocuments,
        deleteOrderDocument,
        isUploadingOrderDocuments,
        failedDocumentUploads,
        numDocumentsToUpload,
        updateAssignedCustomerUser,
        addContact,
        deleteContact,
        updateContact,
        addMandator,
        deleteMandator,
        updateMandator,
        updateDocuments,
        isPatchingOrder,
        tasksInOpen,
        tasksInDone,
        isTasksPending,
        threads,
        isThreadsPending,
        customerUsers,
        isCustomerUsersPending,
        appraiserUsers,
        isAppraiserUsersPending,
        externalParticipants,
        isExternalParticipantsPending,
        documents,
        generateDocumentsZipUrl,
        isGeneratingDocumentsZipUrl,
        generateImagesZipUrl,
        isGeneratingImagesZipUrl,
        isPatchingAssignedCustomerUsers,
        currentUser,
        updateAssignedObservers,
        isAssignedObserversPending,
        addAssignedObserver,
        removeAssignedObserver,
        latestDraftPlausibility,
        showPlausibility,
        isLatestDraftPlausibilityPending,
        valueCheckForOrder,
        externalParticipantMailbox,
    } = useOrderDetailsPageData({ onPatchAssignedCustomerUsersSuccess: () => setIsEditCustomerAssignmentFormOpen(false) })

    // otherwise would be hard to find where to edit observers, when getting navigated to this page as place to edit order observers
    const { isEditMode: isEditingObservers, setIsEditMode: setIsEditingObservers } = useFormQueryParams()

    const assetTitle = assembleAssetTitle({ customer_asset_reference: orderOverview?.customer_asset_reference, ...orderOverview?.address })
    const title = joinDefinedStrings([t('title'), assetTitle])
    const breadCrumbs = [
        { title: t('allOrders'), link: STATIC_APP_PATHS.orderListPage, params: getLatestUrlParams('client-order-filter') },
        { title },
    ]
    const orderKeyFigures = getOrderKeyFigures()
    const valuationKeyFigures = getValuationKeyFigures()
    const images = getImages()
    const numberOfTasks = tasksInOpen.length + tasksInDone.length

    const imageUploadInputRef = useRef<HTMLInputElement>(null)
    const [isEditModalOpen, setIsEditModalOpen] = useState(false)
    const [isUploadModalOpen, setIsUploadModalOpen] = useState(false)
    const isModalOpen = isEditModalOpen || isUploadModalOpen
    const mandatorFormLabels = {
        name: t('mandatorList.form.name'),
        street: t('mandatorList.form.street'),
        houseNumber: t('mandatorList.form.houseNumber'),
        zip: t('mandatorList.form.zip'),
        city: t('mandatorList.form.city'),
        country: t('mandatorList.form.country'),
    }
    const mandatorListCopy = {
        addButtonLabel: t('mandatorList.addButton'),
        addModalTitle: t('mandatorList.addModalTitle'),
        item: {
            edit: t('mandatorList.item.edit'),
            delete: t('mandatorList.item.delete'),
            editModalTitle: t('mandatorList.item.editModalTitle'),
            formLabels: mandatorFormLabels,
        },
        formLabels: mandatorFormLabels,
    }
    const contactFormCopy = {
        salutation: t('contactList.form.salutation'),
        salutationOptions: {
            mr: t('contactList.form.salutationOptions.mr'),
            ms: t('contactList.form.salutationOptions.ms'),
            dr: t('contactList.form.salutationOptions.dr'),
            mrDr: t('contactList.form.salutationOptions.mrDr'),
            msDr: t('contactList.form.salutationOptions.msDr'),
            placeholder: t('contactList.form.salutationOptions.placeholder'),
        },
        name: t('contactList.form.name'),
        role: t('contactList.form.role'),
        company: t('contactList.form.company'),
        street: t('contactList.form.street'),
        houseNumber: t('contactList.form.houseNumber'),
        zip: t('contactList.form.zip'),
        city: t('contactList.form.city'),
        email: t('contactList.form.email'),
        invalidEmail: t('contactList.form.invalidEmail'),
        phoneNumber: t('contactList.form.phoneNumber'),
        invalidPhoneNumber: t('contactList.form.invalidPhoneNumber'),
        note: t('contactList.form.note'),
        infoBox: t('contactList.form.infoBox'),
    }
    const contactListCopy = {
        addButtonLabel: t('contactList.addButton'),
        addModalTitle: t('contactList.addModalTitle'),
        item: {
            edit: t('contactList.item.edit'),
            delete: t('contactList.item.delete'),
            editModalTitle: t('contactList.item.editModalTitle'),
            form: contactFormCopy,
        },
        form: contactFormCopy,
    }
    const userLinkCopy = useUserLinkCopy()
    const customerAssignmentCopy = {
        addButton: t('customerAssignment.addButton'),
        modalTitle: t('customerAssignment.modalTitle'),
        cancel: t('customerAssignment.cancel'),
        duplicateWarning: t('customerAssignment.duplicateWarning'),
        assigneeLabel: t('customerAssignment.assigneeLabel'),
        removeTooltip: t('customerAssignment.removeTooltip'),
        addMore: t('customerAssignment.addMore'),
    }
    const headerObserversCopy = {
        protectedTooltip: t('observers.header.protectedTooltip'),
        platformUserTooltip: t('observers.header.platformUserTooltip'),
        observingTooltip: t('observers.header.observingTooltip'),
        notObservingTooltip: t('observers.header.notObservingTooltip'),
        addObserverTooltip: t('observers.header.addObserverTooltip'),
        observerGroup: t('observers.header.observerGroup'),
        disableObservationAriaLabel: t('observers.header.disableObservationAriaLabel'),
        enableObservationAriaLabel: t('observers.header.enableObservationAriaLabel'),
    }
    const documentUploadModalCopy = {
        uploadTitle: t('documents.modal.uploadTitle'),
        editTitle: t('documents.modal.editTitle'),
        createButton: t('documents.modal.createButton'),
        confirmButton: t('documents.modal.confirmButton'),
        cancelButton: t('documents.modal.cancelButton'),
        dropzonePrompt: t('documents.modal.dropzonePrompt'),
        nameLabel: t('documents.modal.nameLabel'),
        tagsLabel: t('documents.modal.tagsLabel'),
        unknownMimeTypeLabel: t('documents.modal.unknownMimeTypeLabel'),
        uploadErrorAlert: {
            title: t('documents.modal.uploadErrorAlert.title'),
            description: t('documents.modal.uploadErrorAlert.description'),
            supportText: t('documents.modal.uploadErrorAlert.supportText'),
        },
    }
    const observersDrawerCopy = {
        title: t('observers.drawer.title'),
        addObserversLabel: t('observers.drawer.addObserversLabel'),
        observeOrder: t('observers.drawer.observeOrder'),
        observerCount: (count: number) => t('observers.drawer.observerCount', { count }),
        editObservers: t('observers.drawer.editObservers'),
        responsible: t('observers.drawer.responsible'),
        responsibleRemoveTooltip: t('observers.drawer.responsibleRemoveTooltip'),
        infoTitle: t('observers.drawer.infoTitle'),
        infoText: t('observers.drawer.infoText'),
        notificationSettings: t('observers.drawer.notificationSettings'),
    }
    const taskPreviewCopy = {
        createTask: t('tasks.createTask'),
        statusLabels: {
            open: t('tasks.status.open'),
            done: t('tasks.status.done'),
        },
        showMore: t('tasks.preview.showMore'),
        showLess: t('tasks.preview.showLess'),
    }
    const threadCopy = {
        noMessages: t('messages.preview.thread.noMessages'),
        deletedMessage: t('messages.preview.thread.deletedMessage'),
        createdBy: (userName: string) => t('messages.preview.thread.createdBy', { userName }),
        createdAutomatically: t('messages.preview.thread.createdAutomatically'),
    }
    const threadPreviewCopy = {
        emptyStateText: t('messages.emptyState'),
        startThread: t('messages.startConversation'),
        showMore: t('messages.preview.showMore'),
        showLess: t('messages.preview.showLess'),
        item: {
            noTitle: t('messages.preview.noTitle'),
            noMessagesInThread: t('messages.preview.noMessagesInThread'),
            notifiedUsersTooltip: t('messages.preview.notifiedUsersTooltip'),
            thread: threadCopy,
        },
    }

    const closeModal = () => {
        setIsEditModalOpen(false)
        setIsUploadModalOpen(false)
    }

    if (
        isOrderOverviewLoading ||
        isCustomerUsersPending ||
        isAppraiserUsersPending ||
        isExternalParticipantsPending ||
        isTasksPending ||
        isThreadsPending
    ) {
        return <OrderDetailsPageLoading />
    }

    if (!orderOverview) {
        return (
            <ClientPortalPageWithSidebarTemplate withMaxWidth breadCrumbs={breadCrumbs} title={title} sidebarComponent={<OrderSidebar />}>
                <EmptyState style={{ marginTop: '100px' }} title={t('emptyStateTitle')} size="enormous" />
            </ClientPortalPageWithSidebarTemplate>
        )
    }

    const assigneeKeyFigures = [
        {
            label: t('assigneeUser'),
            value: (
                <CustomerUserAssignment
                    customerUsers={customerUsers}
                    open={isEditCustomerAssignmentFormOpen}
                    setOpen={setIsEditCustomerAssignmentFormOpen}
                    assignedCustomerUserIds={orderOverview?.assigned_customer_users}
                    isPersistingAssignedCustomerUsers={isPatchingAssignedCustomerUsers}
                    onSubmitCallback={(newAssignedCustomerUserIds) => updateAssignedCustomerUser(newAssignedCustomerUserIds)}
                    modalPrimaryButtonLabel={t('save')}
                    copy={customerAssignmentCopy}
                    userLinkCopy={userLinkCopy}
                />
            ),
        },
    ]

    const observerUUIDs = orderOverview.observers ?? []

    const currentUserObserverIndex = observerUUIDs.findIndex((observerId) => observerId === currentUser?.uuid)
    const isCurrentUserAssignedCustomer = currentUser?.id ? orderOverview.assigned_customer_users?.includes(currentUser.id) : false

    const appraiserAssignmentUUIDs = (
        [
            orderOverview.appraiser_assignment?.main,
            orderOverview.appraiser_assignment?.secondary,
            orderOverview.appraiser_assignment?.tertiary,
        ].filter(Boolean) as string[]
    ).filter(isUuid)

    const isCurrentUserAssignedAppraiser = currentUser?.uuid ? appraiserAssignmentUUIDs.includes(currentUser.uuid) : false
    const isCurrentUserProtectedObserver = isCurrentUserAssignedCustomer || isCurrentUserAssignedAppraiser
    const isCurrentUserPlatformUser = !!currentUser?.is_platform_user
    const shouldDisableObserverRemovalOption = isCurrentUserProtectedObserver && currentUserObserverIndex !== -1

    const IconButtonOptions: Option[] = [
        {
            title: t('observers.edit'),
            callback: () => setIsEditingObservers(true),
            icon: <UserCircleIcon />,
        },
    ]

    if (currentUser?.uuid) {
        if (currentUserObserverIndex !== -1) {
            IconButtonOptions.push({
                title: t('observers.stopObserving'),
                callback: () => removeAssignedObserver(currentUserObserverIndex),
                icon: <EyeSlashIcon />,
                disabled: shouldDisableObserverRemovalOption,
            })
        } else {
            IconButtonOptions.push({
                title: t('observers.observe'),
                callback: () => addAssignedObserver(currentUser.uuid),
                icon: <EyeIcon />,
                disabled: isCurrentUserPlatformUser,
            })
        }
    }

    const navigateToNotificationSettings = () => {
        if (currentUser?.role === 'appraiser') {
            setIsEditingObservers(false)
            window.open(`${getAppraiserUIBaseUrl()}/orders?settings=notifications`, '_blank')
            return
        }
        const search = new URLSearchParams(location.search)
        search.delete('edit')
        void navigate(
            getUserSettingsModalUrl({
                pathname: location.pathname,
                search,
                hash: location.hash,
                tab: 'notifications',
            })
        )
    }

    return (
        <ClientPortalPageWithSidebarTemplate withMaxWidth breadCrumbs={breadCrumbs} sidebarComponent={<OrderSidebar />}>
            <Stack direction="column" marginBottom={tokens.SizingSpacing400} marginTop={tokens.SizingSpacing400}>
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                    <Stack direction="row" alignItems="center" gap={tokens.SizingSpacing100}>
                        <Headline variant="headline1" title={title} />
                        {!!externalParticipantMailbox && (
                            <Box width={'fit-content'}>
                                <ContentChip size="medium" color="turquoise" label={t('external')} icon={<EnvelopeIcon />} />
                            </Box>
                        )}
                    </Stack>
                    <Stack direction="row" gap={tokens.SizingSpacing150} alignItems="center">
                        <LoadingIndicator isPending={isAssignedObserversPending} />
                        <HeaderObservers
                            observerUUIDs={observerUUIDs}
                            users={[...customerUsers, ...appraiserUsers]}
                            externalParticipantUsers={externalParticipants}
                            currentUser={currentUser}
                            replaceObserverUUIDs={updateAssignedObservers}
                            setIsEditingObservers={setIsEditingObservers}
                            disableCurrentUserToggle={isCurrentUserProtectedObserver}
                            copy={headerObserversCopy}
                            userLinkCopy={userLinkCopy}
                        />
                        <IconButtonWithOptions variant="tertiary" tooltip={t('options')} size="medium" options={IconButtonOptions} />
                    </Stack>
                </Stack>
                {externalParticipantMailbox && (
                    <Stack direction="row" gap={tokens.SizingSpacing025} alignItems="center">
                        <ContentChip size="medium" color="turquoise" label={externalParticipantMailbox} icon={<AtIcon />} />
                        <IconButton
                            size="small"
                            variant="tertiary"
                            icon={<CopyIcon />}
                            onClick={() => navigator.clipboard.writeText(externalParticipantMailbox)}
                        />
                    </Stack>
                )}
            </Stack>
            <ObserversDrawer
                currentUser={currentUser}
                observerUUIDs={observerUUIDs}
                users={[...customerUsers, ...appraiserUsers]}
                externalParticipantUsers={externalParticipants}
                isEditObservers={isEditingObservers}
                setEditObservers={setIsEditingObservers}
                persistObservers={updateAssignedObservers}
                isPersistingObservers={isAssignedObserversPending}
                emptyObserversMessage={t('observers.empty')}
                canEditOtherRoles={false}
                addObserver={addAssignedObserver}
                removeObserver={removeAssignedObserver}
                orderId={orderId}
                orderAssignedAppraiserId={orderOverview.appraiser_assignment?.main}
                orderAssignedCustomerUsers={orderOverview.assigned_customer_users}
                isOrderPending={isOrderOverviewLoading}
                navigateToNotificationSettings={navigateToNotificationSettings}
                copy={observersDrawerCopy}
                userLinkCopy={userLinkCopy}
            />
            <Grid container spacing={tokens.SizingSpacing600}>
                <Grid item xs={6} md={6}>
                    <AssetInfoBox
                        imageSrc={orderOverview.asset_thumbnail_image_url}
                        fallbackImageSrc={orderOverview.asset_image_url}
                        assetId={orderOverview.asset_uuid}
                        customerAssetReference={orderOverview.customer_asset_reference}
                        address={orderOverview.address}
                    />
                </Grid>
                <Grid item xs={6} md={6}>
                    <OrderStatusInfoBox
                        status={orderOverview.status}
                        statusMessage={orderOverview.status_message}
                        displayDate={orderOverview.status === 'inspection_scheduled' ? orderOverview.inspection_date : undefined}
                    />
                </Grid>
            </Grid>
            <PageRowTemplate style={{ flexWrap: 'wrap-reverse' }}>
                <>
                    <HeadlineWithSpacing variant="headline2" title={t('sections.assignees')} />
                    <TabularInfo rows={assigneeKeyFigures} />
                    <AppraiserSection
                        orderId={orderOverview.uuid}
                        appraiserAssignment={orderOverview.appraiser_assignment}
                        appraiserUsers={appraiserUsers}
                        externalParticipants={externalParticipants}
                        hasAsset={!!orderOverview.asset_uuid}
                        userLinkCopy={userLinkCopy}
                    />
                    <Box marginTop={tokens.SizingSpacing800}>
                        <Headline
                            variant="headline2"
                            title={tTerminology('mandators')}
                            withSpacing
                            iconButtons={[
                                {
                                    icon: <PlusCircleIcon />,
                                    onClick: () => setIsAddMandatorFormOpen(true),
                                    tooltip: t('mandatorList.addButton'),
                                    ariaLabel: t('mandatorList.addButton'),
                                },
                            ]}
                        />
                    </Box>
                    <MandatorList
                        mandators={orderOverview.mandators}
                        isAddMandatorFormOpen={isAddMandatorFormOpen}
                        setIsAddMandatorFormOpen={setIsAddMandatorFormOpen}
                        addMandator={addMandator}
                        updateMandator={updateMandator}
                        deleteMandator={deleteMandator}
                        copy={mandatorListCopy}
                    />
                    <Box marginTop={tokens.SizingSpacing800}>
                        <Headline
                            variant="headline2"
                            title={t('sections.externalContacts')}
                            withSpacing
                            iconButtons={[
                                {
                                    icon: <PlusCircleIcon />,
                                    onClick: () => setIsAddContactFormOpen(true),
                                    tooltip: t('contactList.addButton'),
                                    ariaLabel: t('contactList.addButton'),
                                },
                            ]}
                        />
                    </Box>
                    <ContactList
                        contacts={orderOverview.contacts}
                        isAddContactFormOpen={isAddContactFormOpen}
                        setIsAddContactFormOpen={setIsAddContactFormOpen}
                        addContact={addContact}
                        updateContact={updateContact}
                        deleteContact={deleteContact}
                        emptyStateText={t('contacts.emptyState')}
                        copy={contactListCopy}
                        userLinkCopy={userLinkCopy}
                    />
                    <HeadlineWithSpacing variant="headline2" title={t('sections.orderData')} />
                    <TabularInfo rows={orderKeyFigures} labelMinWidth="175px" />
                    <HeadlineWithSpacing variant="headline2" title={t('sections.report')} />
                    <TabularInfo rows={valuationKeyFigures} labelMinWidth="175px" />
                </>
                <>
                    <Box marginTop={tokens.SizingSpacing400}>
                        <ValuationSection
                            hasFinalizedValuation={orderOverview.valuation_status === 'finalised'}
                            finalizedValuationId={orderOverview.valuation_uuid}
                            valuationDate={orderOverview.valuation_date}
                            valuationStatus={orderOverview.valuation_status}
                            assetId={orderOverview.asset_uuid}
                            drafts={orderOverview.drafts}
                            appraiserAssignment={orderOverview.appraiser_assignment}
                            appraiserUsers={appraiserUsers}
                            users={[...customerUsers, ...appraiserUsers]}
                            externalAppraisingEntityName={orderOverview.external_appraising_entity_name}
                            observerUUIDs={orderOverview.observers}
                            latestDraftPlausibility={latestDraftPlausibility}
                            showPlausibility={showPlausibility}
                            isLatestDraftPlausibilityPending={isLatestDraftPlausibilityPending}
                            valueCheckForOrder={valueCheckForOrder}
                            userLinkCopy={userLinkCopy}
                        />
                    </Box>
                    <Stack
                        direction="row"
                        alignItems="center"
                        width="100%"
                        justifyContent="space-between"
                        gap={tokens.SizingSpacing200}
                        marginTop={tokens.SizingSpacing800}
                        marginBottom={tokens.SizingSpacing150}
                    >
                        <Stack direction="row" alignItems="center" gap={tokens.SizingSpacing100}>
                            <Headline
                                variant="headline2"
                                title={tTerminology('tasks')}
                                fullWidth={false}
                                iconButtons={[
                                    {
                                        icon: <PlusCircleIcon />,
                                        onClick: () => navigate(`/orders/${orderOverview.uuid}/tasks?create=true`),
                                        tooltip: t('tasks.addTaskTooltip'),
                                        ariaLabel: t('tasks.addTaskTooltip'),
                                    },
                                ]}
                            />
                            {!isTasksPending && numberOfTasks > 0 && (
                                <Typography variant="textS" color={tokens.ColorTextDefault}>
                                    {t('openTasks', { count: tasksInOpen.length })}
                                </Typography>
                            )}
                        </Stack>
                        <TinyButton
                            label={t('tasks.allTasks')}
                            leftIcon={<ClipboardTextIcon size={tokens.SizingIconS} />}
                            rightIcon={<ArrowUpRightIcon size={tokens.SizingIconS} />}
                            onClick={() => navigate(`/orders/${orderOverview.uuid}/tasks`)}
                        />
                    </Stack>
                    <TaskListPreview
                        tasksDone={tasksInDone}
                        activeTasks={tasksInOpen}
                        numberOfTasks={numberOfTasks}
                        users={[...customerUsers, ...appraiserUsers]}
                        emptyStateText={t('tasks.emptyState')}
                        onClickAddTask={() => navigate(`/orders/${orderOverview.uuid}/tasks?create=true`)}
                        onItemClick={(task) => navigate(`/orders/${task.order.uuid}/tasks?task=${task.uuid}`)}
                        copy={taskPreviewCopy}
                        userLinkCopy={userLinkCopy}
                    />
                    <Stack
                        direction="row"
                        alignItems="center"
                        width="100%"
                        justifyContent="space-between"
                        gap={tokens.SizingSpacing200}
                        marginTop={tokens.SizingSpacing800}
                        marginBottom={tokens.SizingSpacing150}
                    >
                        <Headline
                            variant="headline2"
                            title={tTerminology('messages')}
                            fullWidth={false}
                            iconButtons={[
                                {
                                    icon: <PlusCircleIcon />,
                                    onClick: () => navigate(`/orders/${orderId}/threads?create=true`),
                                    tooltip: t('messages.addConversationTooltip'),
                                    ariaLabel: t('messages.addConversationTooltip'),
                                },
                            ]}
                        />
                        <TinyButton
                            label={t('messages.allThreads')}
                            leftIcon={<ChatsCircleIcon size={tokens.SizingIconS} />}
                            rightIcon={<ArrowUpRightIcon size={tokens.SizingIconS} />}
                            onClick={() => navigate(`/orders/${orderOverview.uuid}/threads`)}
                        />
                    </Stack>
                    <ThreadListPreview
                        threads={threads}
                        emptyStateText={t('messages.emptyState')}
                        onNewThreadClick={() => navigate(`/orders/${orderId}/threads?create=true`)}
                        onItemClick={(thread) => navigate(`/orders/${orderId}/threads?thread=${thread.uuid}`)}
                        copy={threadPreviewCopy}
                        userLinkCopy={userLinkCopy}
                        locale={locale}
                    />
                </>
            </PageRowTemplate>

            <Stack direction="column" width="100%" gap={tokens.SizingSpacing075} marginTop={tokens.SizingSpacing400}>
                <Stack direction="row" width="100%" justifyContent="space-between" alignItems="center">
                    <Headline variant="headline2" title={tTerminology('documents')} />
                    <Stack direction="row" gap={tokens.SizingSpacing200} alignItems="center">
                        {isDownloadingDocument && <LoadingIndicator height={1} isPending />}
                        {documents.length > 0 && (
                            <TinyButton
                                label={t('documents.edit')}
                                onClick={() => setIsEditModalOpen(true)}
                                leftIcon={<PencilSimpleIcon />}
                            />
                        )}
                        <TinyButton label={t('documents.add')} leftIcon={<PlusCircleIcon />} onClick={() => setIsUploadModalOpen(true)} />
                        {!isEmpty(documents) && orderId && generateDocumentsZipUrl && (
                            <TinyButton
                                label={t('documents.downloadAll')}
                                leftIcon={isGeneratingDocumentsZipUrl ? <LoadingIndicator height={1} isPending /> : <DownloadSimpleIcon />}
                                onClick={() => generateDocumentsZipUrl(orderOverview.uuid)}
                                disabled={isGeneratingDocumentsZipUrl}
                            />
                        )}
                    </Stack>
                </Stack>
                {isEmpty(documents) && !isUploadingOrderDocuments && !numDocumentsToUpload && (
                    <Typography variant="textS" color={tokens.ColorTextDefault}>
                        {t('documents.emptyText')}
                    </Typography>
                )}
                {isEmpty(documents) && !isUploadingOrderDocuments && !numDocumentsToUpload && (
                    <TinyButton label={t('documents.uploadFiles')} onClick={() => setIsUploadModalOpen(true)} leftIcon={<PlusIcon />} />
                )}
                {(!isEmpty(documents) || isUploadingOrderDocuments) && (
                    <DocumentTable
                        orderId={orderOverview.uuid}
                        documents={documents}
                        numDocumentsToUpload={numDocumentsToUpload}
                        deleteDocument={deleteOrderDocument}
                        failedDocumentUploads={failedDocumentUploads}
                        isUploading={isUploadingOrderDocuments}
                        downloadDocument={(documentId) => downloadDocument({ documentId, orderId: orderOverview.uuid })}
                        generateZipUrl={() => generateDocumentsZipUrl(orderOverview.uuid)}
                        isGeneratingDocumentsZipUrl={isGeneratingDocumentsZipUrl}
                        openModal={() => setIsUploadModalOpen(true)}
                        locale={locale}
                        copy={{
                            nameHeader: t('documents.table.nameHeader'),
                            tagsHeader: t('documents.table.tagsHeader'),
                            dateHeader: t('documents.table.dateHeader'),
                            downloadAction: t('documents.table.downloadAction'),
                            deleteAction: t('documents.table.deleteAction'),
                            deleteModalTitle: t('documents.table.deleteModalTitle'),
                            deleteModalPrimaryAction: t('documents.table.deleteModalPrimaryAction'),
                            deleteModalSecondaryAction: t('documents.table.deleteModalSecondaryAction'),
                            deleteModalDescription: t('documents.table.deleteModalDescription'),
                        }}
                    />
                )}
            </Stack>

            <Stack direction="column" gap={tokens.SizingSpacing075} marginTop={tokens.SizingSpacing400}>
                <Stack direction="row" width="100%" justifyContent="space-between" alignItems="center">
                    <Headline variant="headline2" title={tTerminology('images')} />
                    <Stack direction="row" gap={tokens.SizingSpacing200} alignItems="center">
                        <TinyButton
                            label={t('images.add')}
                            leftIcon={<PlusCircleIcon />}
                            onClick={() => imageUploadInputRef.current?.click()}
                        />
                        {!isEmpty(images) && orderId && generateImagesZipUrl && (
                            <TinyButton
                                label={t('images.downloadAll')}
                                leftIcon={isGeneratingImagesZipUrl ? <LoadingIndicator height={1} isPending /> : <DownloadSimpleIcon />}
                                onClick={() => generateImagesZipUrl(orderOverview.uuid)}
                                disabled={isGeneratingImagesZipUrl}
                            />
                        )}
                    </Stack>
                </Stack>
                {isEmpty(images) && !isUploadingOrderImages && !numImagesToUpload && (
                    <Typography variant="textS" color={tokens.ColorTextDefault}>
                        {t('images.emptyText')}
                    </Typography>
                )}
                <input
                    ref={imageUploadInputRef}
                    hidden
                    multiple
                    onChange={(e) => {
                        if (e.target.files) {
                            void uploadOrderImages(Array.from(e.target.files))
                        }
                        e.target.value = ''
                    }}
                    type="file"
                    accept={'.png,.jpg,.jpeg,.svg,.tif,.tiff'}
                />
                {isEmpty(images) && !isUploadingOrderImages && !numImagesToUpload && (
                    <div style={{ marginBottom: tokens.SizingSpacing150 }}>
                        <TinyButton
                            label={t('images.upload')}
                            leftIcon={<PlusIcon />}
                            onClick={() => imageUploadInputRef.current?.click()}
                        />
                    </div>
                )}
                <ImageGallery
                    orderId={orderOverview.uuid}
                    images={images}
                    numImagesToUpload={numImagesToUpload}
                    deleteImage={deleteOrderImage}
                    isUploading={isUploadingOrderImages}
                    failedImageUploads={failedImageUploads}
                />
            </Stack>

            <FileUploadModal
                isOpen={isModalOpen}
                close={closeModal}
                isEditing={isEditModalOpen}
                isPersisting={isPatchingOrder}
                isUploading={isUploadingOrderDocuments}
                updateDocuments={updateDocuments}
                documents={documents}
                numDocumentsToUpload={numDocumentsToUpload}
                failedDocumentUploads={failedDocumentUploads}
                downloadDocument={(documentId) => downloadDocument({ documentId, orderId: orderOverview.uuid })}
                isDownloadingDocument={isDownloadingDocument}
                deleteDocument={deleteOrderDocument}
                uploadDocuments={uploadOrderDocuments}
                tagOptions={documentTagOptions}
                copy={documentUploadModalCopy}
            />
        </ClientPortalPageWithSidebarTemplate>
    )
}
